/* md-collection-analytics.js: list analytics for md-product-card (architecture.md 12). Loaded with defer by any
   section that renders cards; runs once per page.
   - view_item_list: once per distinct result set of a [data-md-list] list (list id + item ids), when 50 percent of it,
     or half the viewport for tall grids, is visible.
   - select_item: click on a card link inside a [data-md-list] container.
   - filter_apply: a change in the collection or search facet forms, or an active-filter remove link.
   Transport: Shopify.analytics.publish (no-op when missing). Never sends prices it did not read from the card. */
(() => {
  if (window.mdListAnalytics) return;
  window.mdListAnalytics = true;

  const publish = (name, payload) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* analytics must never break the page */ }
  };
  const item = (card) => {
    try {
      const d = JSON.parse(card.dataset.mdItem);
      d.index = (parseInt(card.dataset.mdIndex, 10) || 0) + 1;
      return d;
    } catch (e) { return null; }
  };
  const listMeta = (list) => ({
    item_list_id: list.dataset.mdList,
    item_list_name: list.dataset.mdListName || list.dataset.mdList,
  });

  const seen = new WeakSet();
  /* CR-C-4: one view_item_list per distinct result set of a list: key = list id + the ordered item ids. Module-level,
     so it survives Dawn's Section Rendering replacement of the grid (a new element with the same list and items
     does not fire again; a filtered set with different items does, once). */
  const fired = new Set();
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || seen.has(e.target)) return;
          if (e.intersectionRatio < 0.5 && e.intersectionRect.height < window.innerHeight * 0.5) return;
          seen.add(e.target);
          io.unobserve(e.target);
          const items = [...e.target.querySelectorAll('[data-md-item]')].map(item).filter(Boolean);
          const key = e.target.dataset.mdList + '|' + items.map((i) => i.item_id).join(',');
          if (!items.length || fired.has(key)) return;
          fired.add(key);
          publish('view_item_list', { ...listMeta(e.target), items });
        });
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] })
    : null;
  /* CR-C-11: mirror Dawn's filtered count (#ProductCount data-product-count) into the collection intro. */
  const syncCount = () => {
    const out = document.querySelector('[data-md-count]');
    const n = parseInt(document.getElementById('ProductCount')?.dataset.productCount, 10);
    if (!out || Number.isNaN(n)) return;
    const text = (n === 1 ? out.dataset.mdCountOne : out.dataset.mdCountMany).replace('[count]', n);
    if (out.textContent !== text) out.textContent = text;
  };
  const scan = () => {
    syncCount();
    if (!io) return;
    document.querySelectorAll('[data-md-list]').forEach((list) => {
      if (!seen.has(list) && !list.dataset.mdObserved) {
        list.dataset.mdObserved = '1';
        io.observe(list);
      }
    });
  };

  document.addEventListener('click', (evt) => {
    const link = evt.target.closest('a');
    const card = link && link.closest('[data-md-item]');
    const list = card && card.closest('[data-md-list]');
    if (!list) return;
    const d = item(card);
    if (d) publish('select_item', { ...listMeta(list), items: [d] });
  });

  /* filter_apply: Dawn's facet forms (facets.liquid) debounce at 800ms; read the form once the input settles. */
  const FORMS = '#FacetFiltersForm, #FacetFiltersFormMobile, #FacetSortForm, #FacetSortDrawerForm';
  let timer;
  const sendFilters = (params) => {
    const keys = [...new Set([...params.keys()].filter((k) => k.startsWith('filter.')))];
    publish('filter_apply', { filter_keys: keys, sort: params.get('sort_by') || '' });
  };
  const onFacet = (evt) => {
    const form = evt.target.closest && evt.target.closest(FORMS);
    if (!form) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const params = new URLSearchParams(new FormData(form));
      const current = new URLSearchParams(location.search);
      if (!params.has('sort_by') && current.has('sort_by')) params.set('sort_by', current.get('sort_by'));
      sendFilters(params);
    }, 900);
  };
  document.addEventListener('input', onFacet);
  document.addEventListener('change', onFacet);
  document.addEventListener('click', (evt) => {
    const remove = evt.target.closest && evt.target.closest('facet-remove a, .active-facets a');
    if (!remove || !remove.href) return;
    try { sendFilters(new URL(remove.href, location.href).searchParams); } catch (e) { /* ignore */ }
  });

  /* Facet re-renders replace the grid (facets.js renderProductGridContainer): watch for new lists. */
  let pending = false;
  const mo = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; scan(); });
  });
  const start = () => {
    scan();
    mo.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
